import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import styles from './manage-subscription.module.css';
import Link from 'next/link';
import { Button, LinearProgress, Stack } from '@mui/material';
import Head from 'next/head';

function ManageSubscriptions() {
  const router = useRouter();
  const [userId, setUserId] = useState('');
  const [eventId, setEventId] = useState('');
  const [eventSlotId, setEventSlotId] = useState('');
  const [action, setAction] = useState('');
  const [showOptions, setShowOptions] = useState(true);
  const [apiStatus, setApiStatus] = useState('idle');
  const [apiMessage, setApiMessage] = useState('');
  const [indicator, setIndicator] = useState(false);

  useEffect(() => {
    const { user_id, event_id, event_slot_id, action: queryAction, indicator: queryIndicator } = router.query;
    if (user_id && event_id && queryAction === 'unsubscribe') {
      setUserId(user_id);
      setEventId(event_id);
      setEventSlotId(event_slot_id || '');
      setAction(queryAction);
      if (queryIndicator === 'true') {
        setIndicator(true);
      } else {
        setIndicator(false);
      }
    } else {
      setApiStatus('error');
      setApiMessage('Invalid or missing subscription details in the link.');
    }
  }, [router.query]);

  const handleUnsubscribe = async (type) => {
    if (apiStatus === 'loading') return;
    setApiStatus('loading');

    let payload = {
      user_id: userId,
      event_id: eventId,
      action: "unsubscribe",
    };

    if (type === "single" && eventSlotId) {
      payload = {
        ...payload,
        event_slot_id: eventSlotId,
        subscription_type: "slot",
        subscribed_to_series: indicator,
      };
    } else if (type === "series") {
      payload = {
        ...payload,
        subscription_type: "event",
        subscribed_to_series: true,
      };
    } else if (type === "single" && !eventSlotId) {
      setApiStatus('error');
      setApiMessage('Missing event slot details. Please use a valid unsubscription link.');
      setShowOptions(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_CLOUDFRONT}event/event-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status == "success") {
        setApiStatus('success');
        setApiMessage(`You’ve successfully unsubscribed from this ${type === 'single' ? 'event' : 'event series'}.`);
        setShowOptions(false);
      } else {
        setApiStatus('error');
        setApiMessage(data.message || `We couldn’t process your unsubscription at the moment. Please try again.`);
        setShowOptions(false);
      }
    } catch (error) {
      setApiStatus('error');
      setApiMessage('We couldn’t connect to the server. Please check your connection or try again later.');
      setShowOptions(false);
    }
  };

  return (
    <>
<Head>
    <title>Manage Subscriptions | School of Our Divine Infinite Being</title>
    <meta
      name="viewport"
      content="width=device-width, initial-scale=1.0"
    ></meta>
    <meta
      name="keywords"
      content="Manage Subscriptions, Account Settings, Billing Information, Subscription Status, School of Our Divine Infinite Being"
    ></meta>
    <meta
      property="og:title"
      content="Manage Your Subscriptions | School of Our Divine Infinite Being"
    ></meta>
    <meta property="og:type" content="website"></meta>
    <meta
      property="og:image"
      content="https://all-frontend-assets.s3.us-east-1.amazonaws.com/transcendentpagan/transcendentpagan_ui_next/og_img/manage_subscriptions_og_img.webp"
    ></meta>
    <meta
      property="og:image:alt"
      content="An icon of a user profile and a settings gear, representing the account management and subscription page for the School of Our Divine Infinite Being."
    ></meta>
    <meta
      property="og:description"
      content="Easily manage your subscriptions for the School of Our Divine Infinite Being. View your current plans, update billing information, and see your subscription history all in one place."
    ></meta>

    <meta property="og:image:width" content="1200"></meta>
    <meta property="og:image:height" content="627"></meta>
    <meta
      property="og:url"
      content="https://www.divineinfinitebeing.com/manage-subscriptions"
    ></meta>

    <meta name="twitter:card" content="summary_large_image"></meta>
    <meta
      property="twitter:image"
      content="https://all-frontend-assets.s3.us-east-1.amazonaws.com/transcendentpagan/transcendentpagan_ui_next/og_img/manage_subscriptions_og_img.webp"
    ></meta>
    <meta
      property="twitter:image:alt"
      content="An icon of a user profile and a settings gear, representing the account management and subscription page for the School of Our Divine Infinite Being."
    ></meta>
    <meta
      name="twitter:url"
      content="https://www.divineinfinitebeing.com/manage-subscriptions"
    ></meta>
    <meta
      name="twitter:title"
      content="Manage Your Subscriptions | School of Our Divine Infinite Being"
    ></meta>
    <meta
      name="twitter:description"
      content="Easily manage your subscriptions for the School of Our Divine Infinite Being. View your current plans, update billing information, and see your subscription history all in one place."
    ></meta>
      <meta name="robots" content="noindex, nofollow" /> 
    <link rel="canonical" href="https://www.divineinfinitebeing.com/manage-subscriptions" />
</Head>





    <div className={styles.confirmation_container}>
      <div className={styles.confirmation_container_sub}>
      <div className={`${styles.confirmation_container_inner} ${showOptions ? styles.confirmation_container_foroptions : ''}`}>
      {showOptions && (
        <div className={styles.showOptionsSec}>
          <h1>Manage Subscriptions</h1>
          <div className={styles.unsubscribe_buttonwrap}>
          <Stack
                direction="row"
                sx={{
                  justifyContent: "center",
                  flexWrap: "wrap",
                  marginTop: "5px",
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => handleUnsubscribe("single")}
                  disabled={apiStatus === 'loading'}
                  sx={{
                    margin: "5px",
                    backgroundColor: "#0c0c0cc4",
                    color: "white",
                    transition:
                      "background-color 0.2s ease-out, transform 0.15s ease-out",
                    "&:hover": {
                      backgroundColor: "#2a2a2acc",
                      transform: "scale(1.03)",
                    },
                    "&:active": {
                      backgroundColor: "#3f3f3fcc",
                      transform: "scale(0.95)",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#0c0c0c80",
                      color: "#c1c1c1 !important",
                      transform: "none",
                    },
                  }}
                >
                  Unsubscribe Single Event
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleUnsubscribe("series")}
                  disabled={apiStatus === 'loading'}
                  sx={{
                    margin: "5px",
                    backgroundColor: "#0c0c0cc4",
                    color: "white",
                    transition:
                      "background-color 0.2s ease-out, transform 0.15s ease-out",
                    "&:hover": {
                      backgroundColor: "#2a2a2acc",
                      transform: "scale(1.03)",
                    },
                    "&:active": {
                      backgroundColor: "#3f3f3fcc",
                      transform: "scale(0.95)",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#0c0c0c80",
                      color: "#c1c1c1 !important",
                      transform: "none",
                    },
                  }}
                >
                  Unsubscribe Series of Event
                </Button>
              </Stack>
          </div>
        </div>
      )}

      

      {apiStatus === 'success' && (
        <div className={styles.successMessage}>
          <div className={styles.successIcon}>
                  <svg
                    className={styles.checkmark}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 52 52"
                  >
                    {" "}
                    <circle
                      className={styles.checkmark__circle}
                      cx="26"
                      cy="26"
                      r="25"
                      fill="none"
                    />{" "}
                    <path
                      className={styles.checkmark__check}
                      fill="none"
                      d="M14.1 27.2l7.1 7.2 16.7-16.8"
                    />
                  </svg>
                </div>
          <h1>Successfully Unsubscribed!</h1>
          <p>{apiMessage}</p>
          <Link href="/" className={styles.button}>
            Go to Homepage
          </Link>
        </div>
      )}

      {apiStatus === 'error' && (
        <div className={styles.errorMessage}>
          <div className={styles.errorIcon}>⚠️</div>
          <h1>Oops! Something went wrong.</h1>
          <p id="errorMessageText">{apiMessage}</p>
          <Link href="/" className={styles.button}>
            Go to Homepage
          </Link>
        </div>
      )}
      {apiStatus === 'loading' && <LinearProgress sx={{ mt: 2, mb: 1 }} />}

      </div>
    </div>
    </div>
    </>
  );
}

export default ManageSubscriptions;